import React, { useState, useEffect, useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../Libs/firebase-config.mjs';

const ImpactSection = ({ darkMode }) => {
  const [impactData, setImpactData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const { scrollY } = useScroll();
  const y2 = useTransform(scrollY, [1000, 2500], [0, -150]);

  useEffect(() => {
    loadImpactData();
  }, []);

  const loadImpactData = async () => {
    try {
      const docRef = doc(db, 'impactSection', 'content');
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        setImpactData(docSnap.data());
      } else {
        // Fallback to default data if no data in Firestore
        setImpactData({
          subtitle: "Ministry Impact",
          title: "A Life of Influence",
          description: "Two decades of transformative ministry bringing deliverance, healing, prosperity, and salvation across Kenya and beyond.",
          impactTitle: "Transformative Impact",
          impactContent: [
            "Through Faith Life Evangelistic Church and international missions, Rev. Peter Kyengo has established a growing presence across Machakos, Kitui, Makueni, Kajiado, and Uasin Gishu counties.",
            "His ministry extends beyond church walls through leadership consulting, capacity building for ministry workers, and mentorship programs for the next generation of leaders.",
            "With academic credentials from Pan African Christian University and international training, he combines theological depth with practical leadership expertise to transform communities."
          ],
          stats: [
            { number: 20, suffix: "+", label: "Years of Active Ministry" },
            { number: 5, suffix: "", label: "Kenyan Counties Reached" },
            { number: 4, suffix: "+", label: "Nations Ministered" },
            { number: 15, suffix: "+", label: "Church Plants Supported" },
            { number: 3, suffix: "", label: "Academic Degrees" },
            { number: 100, suffix: "+", label: "Leaders Mentored" }
          ]
        });
      }
    } catch (err) {
      console.error('Error loading impact section:', err);
      setError('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  // Show loading state
  if (loading) {
    return (
      <section className="relative min-h-screen flex items-center py-32 overflow-hidden">
        <div className="absolute inset-0 z-0 bg-slate-300 animate-pulse"></div>
        <div className={`absolute inset-0 z-10 ${
          darkMode 
            ? 'bg-gradient-to-t from-slate-900 via-slate-900/50 to-transparent' 
            : 'bg-gradient-to-t from-white via-white/30 to-transparent'
        }`} />
        
        <div className="max-w-6xl mx-auto px-8 relative z-20 w-full">
          {/* Header Skeleton */}
          <div className="text-center mb-16 space-y-6">
            <div className={`h-4 bg-slate-400 rounded w-48 mx-auto ${darkMode ? 'bg-slate-600' : ''}`}></div>
            <div className={`h-12 bg-slate-400 rounded w-64 mx-auto ${darkMode ? 'bg-slate-600' : ''}`}></div>
            <div className={`h-8 bg-slate-400 rounded w-96 max-w-full mx-auto ${darkMode ? 'bg-slate-600' : ''}`}></div>
          </div>

          {/* Stats Skeleton */}
          <div className="grid md:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((_, index) => (
              <div key={index} className={`p-8 rounded-2xl ${darkMode ? 'bg-slate-800' : 'bg-white/80'} backdrop-blur-sm border ${darkMode ? 'border-slate-700' : 'border-slate-200'} animate-pulse`}>
                <div className="text-center space-y-4">
                  <div className={`h-12 bg-slate-400 rounded w-32 mx-auto ${darkMode ? 'bg-slate-600' : ''}`}></div>
                  <div className={`h-6 bg-slate-400 rounded w-48 mx-auto ${darkMode ? 'bg-slate-600' : ''}`}></div>
                </div>
              </div>
            ))}
          </div>

          {/* Content Skeleton */}
          <div className={`mt-16 p-8 rounded-2xl ${darkMode ? 'bg-slate-800' : 'bg-white/80'} backdrop-blur-sm border ${darkMode ? 'border-slate-700' : 'border-slate-200'} animate-pulse`}>
            <div className={`h-8 bg-slate-400 rounded w-64 mx-auto mb-6 ${darkMode ? 'bg-slate-600' : ''}`}></div>
            <div className="space-y-4">
              <div className={`h-4 bg-slate-400 rounded w-full ${darkMode ? 'bg-slate-600' : ''}`}></div>
              <div className={`h-4 bg-slate-400 rounded w-5/6 mx-auto ${darkMode ? 'bg-slate-600' : ''}`}></div>
              <div className={`h-4 bg-slate-400 rounded w-4/6 mx-auto ${darkMode ? 'bg-slate-600' : ''}`}></div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Show error state
  if (error) {
    return (
      <section className="relative min-h-screen flex items-center py-32 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div className={`absolute inset-0 z-10 ${
            darkMode 
              ? 'bg-gradient-to-t from-slate-900 via-slate-900/50 to-transparent' 
              : 'bg-gradient-to-t from-white via-white/30 to-transparent'
          }`} />
          <img 
            src="https://images.unsplash.com/photo-1491438590914-bc09fcaaf77a?w=1600&h=1000&fit=crop" 
            alt="Congregation"
            className="w-full h-full object-cover"
          />
        </div>
        
        <div className="max-w-6xl mx-auto px-8 relative z-20 w-full text-center">
          <div className={`${darkMode ? 'bg-slate-800/80' : 'bg-white/80'} backdrop-blur-sm p-12 rounded-2xl border ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
            <div className={`text-lg font-bold ${darkMode ? 'text-red-400' : 'text-red-600'} mb-4`}>
              Error Loading Content
            </div>
            <div className={`text-md ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
              {error}
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Use the actual data from Firestore
  const data = impactData;

  // StatCard Component
  const StatCard = ({ stat, index, darkMode }) => {
    return (
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: index * 0.1 }}
        className={`p-8 rounded-2xl ${darkMode ? 'bg-slate-800/80' : 'bg-white/80'} backdrop-blur-sm border ${darkMode ? 'border-slate-700' : 'border-slate-200'} text-center`}
      >
        <motion.div
          initial={{ scale: 0 }}
          whileInView={{ scale: 1 }}
          viewport={{ once: true }}
          transition={{ type: "spring", stiffness: 200, delay: index * 0.1 + 0.2 }}
          className={`text-5xl md:text-6xl font-bold ${darkMode ? 'text-white' : 'text-slate-900'} mb-4`}
        >
          {stat.number}{stat.suffix}
        </motion.div>
        <div className={`text-lg ${darkMode ? 'text-slate-300' : 'text-slate-700'} font-medium`}>
          {stat.label}
        </div>
      </motion.div>
    );
  };

  return (
    <section className="relative min-h-screen flex items-center py-32 overflow-hidden">
      {/* Background Image - Fixed */}
      <motion.div 
        style={{ y: y2 }}
        className="absolute inset-0 z-0"
      >
        <div className={`absolute inset-0 z-10 ${
          darkMode 
            ? 'bg-gradient-to-t from-slate-900 via-slate-900/50 to-transparent' 
            : 'bg-gradient-to-t from-white via-white/30 to-transparent'
        }`} />
        <img 
          src="https://images.unsplash.com/photo-1491438590914-bc09fcaaf77a?w=1600&h=1000&fit=crop" 
          alt="Congregation"
          className="w-full h-full object-cover"
        />
      </motion.div>

      <div className="max-w-6xl mx-auto px-8 relative z-20 w-full">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className={`text-sm uppercase tracking-widest ${darkMode ? 'text-slate-400' : 'text-slate-600'} font-medium mb-4 font-sans`}>
            {data.subtitle || "Ministry Impact"}
          </div>
          <h2 className={`text-5xl md:text-6xl font-bold ${darkMode ? 'text-white' : 'text-slate-900'} mb-8 font-sans`}>
            {data.title || "A Life of Influence"}
          </h2>
          <p className={`text-xl md:text-2xl ${darkMode ? 'text-slate-300' : 'text-slate-700'} max-w-3xl mx-auto leading-relaxed font-sans`}>
            {data.description || "Two decades of transformative ministry bringing deliverance, healing, prosperity, and salvation across Kenya and beyond."}
          </p>
        </motion.div>

        {/* Statistics Grid */}
        <div className="grid md:grid-cols-3 gap-6">
          {data.stats.map((stat, index) => (
            <StatCard key={index} stat={stat} index={index} darkMode={darkMode} />
          ))}
        </div>

        {/* Additional Impact Description */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
          className={`mt-16 p-8 rounded-2xl ${darkMode ? 'bg-slate-800/80' : 'bg-white/80'} backdrop-blur-sm border ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}
        >
          <h3 className={`text-3xl font-bold ${darkMode ? 'text-white' : 'text-slate-900'} mb-6 font-sans text-center`}>
            {data.impactTitle || "Transformative Impact"}
          </h3>
          <div className={`text-lg ${darkMode ? 'text-slate-300' : 'text-slate-700'} leading-relaxed space-y-4 font-sans`}>
            {data.impactContent && data.impactContent.length > 0 ? (
              data.impactContent.map((paragraph, index) => (
                <p key={index}>
                  {paragraph}
                </p>
              ))
            ) : (
              // Fallback content if no impactContent exists
              <>
                <p>
                  Through Faith Life Evangelistic Church and international missions, Rev. Peter Kyengo has established 
                  a growing presence across Machakos, Kitui, Makueni, Kajiado, and Uasin Gishu counties.
                </p>
                <p>
                  His ministry extends beyond church walls through leadership consulting, capacity building for 
                  ministry workers, and mentorship programs for the next generation of leaders.
                </p>
                <p>
                  With academic credentials from Pan African Christian University and international training, 
                  he combines theological depth with practical leadership expertise to transform communities.
                </p>
              </>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ImpactSection;